import type { WithId } from '@medplum/core';
import type { DocumentReference } from '@medplum/fhirtypes';
import { format, parseISO } from 'date-fns';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { DATE_FORMATS } from '../../utils/date';
import { documentTypeLabel } from './documentTypeLabel';
import { RecordListItem } from './RecordListItem';

export type DocumentsListProps = {
  documents: WithId<DocumentReference>[];
  onSelectDocument?: (id: string) => void;
  selectedDocumentId?: string;
};

/** Row metadata line: upload time, document type, and author, dot-separated, skipping gaps. */
const documentDescription = (doc: WithId<DocumentReference>, t: TFunction<'records'>): string =>
  [
    doc.date ? format(parseISO(doc.date), DATE_FORMATS.DATE_TIME) : undefined,
    documentTypeLabel(doc.content?.[0]?.attachment?.contentType, t),
    doc.author?.[0]?.display,
  ]
    .filter(Boolean)
    .join(' · ');

export const DocumentsList = ({
  documents,
  onSelectDocument,
  selectedDocumentId,
}: DocumentsListProps): React.ReactElement => {
  const { t } = useTranslation('records');

  return (
    <div className="mx-auto w-full max-w-4xl">
      <div className="space-y-2">
        {documents.length > 0 ? (
          documents.map((doc) => (
            <RecordListItem
              key={doc.id}
              title={doc.content?.[0]?.attachment?.title ?? t('document')}
              description={documentDescription(doc, t) || undefined}
              onClick={onSelectDocument ? (): void => onSelectDocument(doc.id) : undefined}
              selected={selectedDocumentId === doc.id}
              testId={`document-list-item-${doc.id}`}
            />
          ))
        ) : (
          <p className="py-8 text-center text-sm text-muted-foreground">{t('noDocumentsFound')}</p>
        )}
      </div>
    </div>
  );
};
